<template>
  <div
    v-click-outside="hideDropdownActions"
    class="sb-action-list"
    data-sb-automation="sb-action-list"
    data-sb-regression="sb-action-list"
    :class="{active: showDropdown, disabled}"
  >
    <SbButton
      ref="activator"
      :variation="variation"
      :size="size"
      class="sb-action-list__activator"
      :disabled="disabled"
      :version="dsComponentDetails.version"
      @click.prevent="toggleDropdownOptions"
    >
      <!--
        @slot The button text
      -->
      <slot>
        <sb-text value="common.actions" />
      </slot>
      <i
        v-show="showDropDownState"
        aria-hidden="true"
        class="sb-action-list__activator-icon sb-action-list__activator-icon--space-left sb-icon-caret-down"
        :class="{'sb-action-list__activator-icon--expanded': showDropdown}"
      />
    </SbButton>
    <SbPopover
      :target="popoverTarget"
      :active="showDropdown"
      :placement="placement"
      orientation="bottom"
      :boundaries-element="boundariesElement"
      :position-fixed="positionFixed"
    >
      <FadeTransition :duration="fadeDuration">
        <SbLoadingPromiseWrapper
          v-show="showDropdown"
          ref="sb-options-popover"
          v-model:loading="isLoading"
          size="xtsm"
          :loading-promise="loadingPromise"
          :classes="loadingClasses"
        >
          <!--
            @slot what to show when no actions available
          -->
          <slot
            v-if="!hasActions"
            name="empty-actions"
          >
            <SbEmptyActions>
              <sb-text :value="emptyActionsText" />
            </SbEmptyActions>
          </slot>
          <!--
            @slot the actions that will appear in the dropdown
          -->
          <slot
            v-if="!hasActionsSlot"
            name="actions"
            :options="actionsInternal"
          />
          <slot
            v-else
            name="actions"
          />
        </SbLoadingPromiseWrapper>
      </FadeTransition>
    </SbPopover>
  </div>
</template>

<script>

import EscapeKeyMixin from '@src/utils/mixins/escapeKey';
import ClickOutsideDirectiveMixin from '@src/utils/directives/clickOutsideDirective';

import SbPopover from '@src/components/Popover/SbPopover.vue';
import FadeTransition from '@src/components/Transitions/Fade.vue';
import SbLoadingPromiseWrapper from '@src/components/Loading/SbLoadingPromiseWrapper.vue';
import SbButton from '@src/components/Button/SbButton.vue';
import SbText from '@src/components/Text/SbText.vue';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';
import SbEmptyActions from '@src/components/ActionList/components/SbEmptyActions.vue';

/**
 * Displays a list of actions when the button is clicked.
 */
export default {
  name: 'SbActionList',
  components: {
    SbEmptyActions,
    SbPopover,
    FadeTransition,
    SbLoadingPromiseWrapper,
    SbButton,
    SbText,
  },
  mixins: [EscapeKeyMixin, ClickOutsideDirectiveMixin, versionedComponentsMixin],
  provide() {
    const { SbActionList } = this;
    return { SbActionList };
  },
  props: {
    /**
     * Positions the container with respect to its orientation.
     * This follows the same rules as flex-box
     * One of: `(start|center|end)`
     */
    placement: {
      type: String,
      default: 'start',
      validator: (value) => value.match(/(center|start|end)/),
    },
    /**
     * The number of actions shown.
    */
    resultsLimit: {
      type: [Number, String],
      default: 5,
    },
    /**
     * List of actions to populate the dropdown.
     * If you provide this prop then the dropdown will do the list iteration for you
     * and expose the option as part of a scoped slot.
     */
    actions: {
      type: Array,
      required: false,
      default: null,
      validator: (value) => {
        return !value || !value.some((element) => !element || typeof element !== 'object');
      },
    },
    /**
     * This is the text to be used if the options list is empty.
     * If you need control over the markup of the empty options, you can use the `empty-options` slot.
     * When using this, it will override the text value with that markup. Please use the `sb-empty-options` component to get some default styling.
     */
    emptyActionsText: {
      type: String,
      default: 'sbActionList.emptyActions',
    },
    /**
     * A promise which will be used to apply loading styles until its resolved/rejected
    */
    loadingPromise: {
      type: Promise,
      default: null,
    },
    /**
     * Disable dropdown from user input
    */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * The element the actionlist will be bound to.
    */
    boundariesElement: {
      type: String,
      default: 'viewport',
    },
    /**
     * Style variation to give additional meaning.
     * `primary, secondary, tertiary, warning, minimal, caution`
    */
    variation: {
      type: String,
      default: 'primary',
      validator: (value) => value.match(/(primary|secondary|tertiary|warning|minimal|caution|link)/),
    },
    /**
     * Set the position of the popover to fixed
     */
    positionFixed: {
      type: Boolean,
      default: false,
    },
    /**
    *  Shows or hides the dropdown icon
    */
    showDropDownState: {
      type: Boolean,
      default: true,
    },
    /**
     * Size of the button to be used for the action list.
     * `extra-small, small, medium, large`
     */
    size: {
      type: String,
      default: 'medium',
      validator: (value) => value.match(/(extra-small|small|medium|large)/),
    },
  },
  emits: [
    /**
    * On click of the action.
    */
    'onAction',
  ],
  data: () => ({
    showDropdown: false,
    isLoading: null,
    SbActionList: {
      actions: [],
      hasProvidedActions: false,
      triggerAction: null,
    },
  }),
  computed: {
    loadingClasses() {
      const classes = ['options-popover'];
      if (this.isLoading) classes.push('options-loading');
      return classes;
    },
    popoverTarget() {
      return this.$refs.activator;
    },
    fadeDuration() {
      return 100;
    },
    actionsInternal() {
      return this.SbActionList.actions;
    },
    hasActions() {
      return this.actionsInternal.length > 0;
    },
    hasActionsSlot() {
      return this.$slots.actions && this.$slots.actions();
    },
  },
  watch: {
    actions: {
      immediate: true,
      handler(newActions) {
        this.SbActionList.actions = (newActions || this.SbActionList.actions || []).filter(Boolean);
        this.calculateHeight();
      },
    },
    showDropdown(show) {
      if (show) this.calculateHeight();
    },
  },
  created() {
    if (this.actions !== null) this.SbActionList.hasProvidedActions = true;
    this.SbActionList.triggerAction = this.triggerAction;
  },
  methods: {
    escapeKeyHandler() {
      if (this.showDropdown) this.hideDropdownActions();
    },
    showDropdownOptions() {
      this.showDropdown = true;
    },
    hideDropdownActions() {
      this.showDropdown = false;
    },
    toggleDropdownOptions() {
      if (this.disabled) return;
      this.showDropdown = !this.showDropdown;
    },
    getPopoverMaxHeight(options = []) {
      let height = 0;
      const length = Math.min(this.resultsLimit, options.length);
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < length; i++) {
        height += options[i].offsetHeight;
      }
      return height;
    },
    calculateHeight() {
      this.$nextTick(() => {
        const options = this.$el?.querySelectorAll('.sb-action');
        if (options?.length > 0 && this.$refs['sb-options-popover']?.$el) {
          this.$refs['sb-options-popover'].$el.style.maxHeight = `${this.getPopoverMaxHeight(options)}px`;
        }
      });
    },
    triggerAction(action) {
      this.$emit('onAction', action);
      this.hideDropdownActions();
    },
  },
};
</script>

<style lang='scss'>
@import "@src/components/Popover/style/sb-popover";

@import '@src/components/ActionList/style/sb-action-list.scss';
</style>
